import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const slides = [
  {
    id: 'v01',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20a%20rainy%20Tokyo%20back%20street%20at%20midnight%20with%20layered%20neon%20signs%20and%20wet%20asphalt%20reflections%20moody%20atmospheric%20deep%20navy%20and%20warm%20amber%20lighting%20soft%20film%20grain%20editorial%20photography%20no%20people&width=1600&height=900&seq=wmzm-hero-01&orientation=landscape',
    eyebrow: '本周焦点 · 日本 / 业余风',
    title: '深夜便利店的城市漫游',
    desc: '手持镜头跟随便利店的灯箱与雨后路面，冷调霓虹与暖色招牌交织，记录一段松弛的都市夜行。',
    meta: ['4K 画质', '12:48', '2026 新片'],
  },
  {
    id: 'v06',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20a%20traditional%20ryokan%20wooden%20corridor%20with%20rising%20steam%20and%20warm%20paper%20lantern%20glow%20at%20night%20moody%20atmospheric%20deep%20navy%20and%20warm%20amber%20lighting%20soft%20film%20grain%20editorial%20photography%20no%20people&width=1600&height=900&seq=wmzm-hero-02&orientation=landscape',
    eyebrow: '编辑推荐 · 日本 / 温泉旅拍',
    title: '温泉旅馆的两小时',
    desc: '从玄关木格栅到走廊尽头的雾气温泉，全程以自然光与纸灯照明拍摄，节奏安静得刚刚好。',
    meta: ['4K 画质', '21:05', '冬季限定'],
  },
  {
    id: 'v10',
    image:
      'https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20an%20empty%20photo%20studio%20with%20softbox%20lights%20and%20seamless%20backdrop%20casting%20long%20warm%20shadows%20moody%20atmospheric%20deep%20navy%20and%20warm%20amber%20lighting%20soft%20film%20grain%20editorial%20photography%20no%20people&width=1600&height=900&seq=wmzm-hero-03&orientation=landscape',
    eyebrow: '合集上新 · 日本 / 女优合集',
    title: '棚拍灯下的三种情绪',
    desc: '同一套布光下完成的三组不同气质拍摄，冷白硬光与暖调柔光交替，展示光位变化带来的画面差异。',
    meta: ['4K 画质', '23:41', '多机位'],
  },
];

export default function HeroCarousel() {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [paused, setPaused] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (paused) return undefined;
    timer.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6500);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [paused]);

  const slide = slides[active];

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : '/search');
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[560px] w-full sm:h-[580px] lg:h-[600px]">
        {slides.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === active ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={item.image}
              alt={`${item.title} - 日韩自拍视频推荐封面`}
              title={`${item.title} - 高清视频在线浏览演示`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 hero-mask" />

        <div className="relative mx-auto flex h-full w-full max-w-[1280px] flex-col justify-center px-4 py-10 md:px-8">
          <div key={slide.id} className="animate-fade-up max-w-[620px]">
            <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-accent-300">
              <span className="inline-block h-[6px] w-[6px] rounded-full bg-accent-500" />
              {slide.eyebrow}
            </p>
            <h1 className="mt-4 font-heading text-3xl font-semibold leading-[1.25] text-background-50 md:text-5xl">
              日韩自拍高清视频
              <br />
              分类浏览与在线观看
            </h1>
            <p className="mt-4 max-w-[460px] text-[13px] leading-7 text-background-200 md:text-sm">
              本期焦点《{slide.title}》— {slide.desc}
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px] text-background-300">
              {slide.meta.map((meta) => (
                <span key={meta} className="rounded-full border border-background-50/25 px-2.5 py-1">
                  {meta}
                </span>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-2 sm:flex-row">
              <Link
                to={`/detail/${slide.id}`}
                className="flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400 sm:w-auto"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-play-circle-line text-base" />
                </span>
                查看焦点内容
              </Link>
              <Link
                to="/category/%E6%83%85%E4%BE%A3%E8%87%AA%E6%8B%8D"
                className="flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 text-sm text-background-50 transition-colors hover:bg-background-50/10 sm:w-auto"
              >
                浏览全部分类
              </Link>
            </div>

            <form
              onSubmit={submit}
              className="mt-6 flex h-11 w-full max-w-[440px] items-center gap-2 rounded-full bg-background-50/95 p-1 pl-4"
            >
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索标题 / 题材 / 标签，例如：温泉"
                className="h-full w-full border-none bg-transparent text-[13px] text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="flex h-9 shrink-0 cursor-pointer items-center whitespace-nowrap rounded-full bg-primary-700 px-4 text-[12px] text-background-50 transition-colors hover:bg-primary-800"
              >
                搜索
              </button>
            </form>
          </div>

          <div className="absolute bottom-6 right-4 flex items-center gap-2 md:right-8">
            {slides.map((item, index) => (
              <button
                key={item.id}
                type="button"
                aria-label={`切换到第 ${index + 1} 张推荐`}
                onClick={() => setActive(index)}
                className={`h-1.5 cursor-pointer rounded-full transition-all ${
                  index === active ? 'w-9 bg-accent-500' : 'w-4 bg-background-50/45 hover:bg-background-50/70'
                }`}
              />
            ))}
            <span className="ml-1 text-[11px] tabular-nums text-background-300">
              0{active + 1} / 0{slides.length}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}